/**
 * Mapping/Matching Answer Tool Utilities
 * Handles configuration, validation, and grading for mapping diagrams
 */

// Default configuration for mapping questions
export const defaultMappingConfig = {
  // Display options
  leftLabel: 'X',
  rightLabel: 'Y',
  showLabels: true,
  ovalShape: true,

  // Items in each column
  leftItems: [{ id: 'L1', value: '', type: 'text' }],
  rightItems: [{ id: 'R1', value: '', type: 'text' }],

  // Mapping rules
  allowMultipleFromLeft: false, // Can one left item connect to multiple right?
  allowMultipleToRight: true, // Can multiple left connect to same right?
  requireAllLeftMapped: true, // Must every left item be connected?

  // Grading configuration
  grading: {
    mode: 'exact', // 'exact' | 'partial'
  },
};

// Generate unique ID for items
export const generateItemId = (prefix) => {
  return `${prefix}${Date.now()}_${Math.random().toString(36).substr(2, 5)}`;
};

// Create a new item with default values
export const createItem = (prefix, value = '', type = 'text') => ({
  id: generateItemId(prefix),
  value,
  type,
});

// Validate if a connection can be made based on config rules
export const validateConnection = (
  fromId,
  toId,
  currentConnections,
  config
) => {
  const { allowMultipleFromLeft, allowMultipleToRight } = config;

  // Check if connection already exists
  const connectionExists = currentConnections.some(
    (c) => c.from === fromId && c.to === toId
  );
  if (connectionExists) {
    return { valid: false, reason: 'Connection already exists' };
  }

  // Check if left item already has a connection (when multiple not allowed)
  if (!allowMultipleFromLeft) {
    const leftHasConnection = currentConnections.some((c) => c.from === fromId);
    if (leftHasConnection) {
      return {
        valid: false,
        reason: 'This item already has a connection',
        replaceExisting: true,
        existingConnection: currentConnections.find((c) => c.from === fromId),
      };
    }
  }

  // Check if right item already has a connection (when multiple not allowed)
  if (!allowMultipleToRight) {
    const rightHasConnection = currentConnections.some((c) => c.to === toId);
    if (rightHasConnection) {
      return {
        valid: false,
        reason: 'This target already has a connection',
      };
    }
  }

  return { valid: true };
};

// Check if answer meets the requireAllLeftMapped rule
export const validateCompleteness = (connections, config) => {
  if (!config.requireAllLeftMapped) {
    return { complete: true };
  }

  const mappedLeftIds = new Set(connections.map((c) => c.from));
  const allLeftIds = config.leftItems.map((item) => item.id);
  const unmappedItems = allLeftIds.filter((id) => !mappedLeftIds.has(id));

  if (unmappedItems.length > 0) {
    return {
      complete: false,
      reason: `${unmappedItems.length} item(s) are not connected`,
      unmappedItems,
    };
  }

  return { complete: true };
};

// Parse answer from JSON string
export const parseAnswer = (answerString) => {
  if (!answerString) {
    return { connections: [] };
  }
  try {
    const parsed = JSON.parse(answerString);
    return {
      connections: Array.isArray(parsed.connections) ? parsed.connections : [],
    };
  } catch {
    return { connections: [] };
  }
};

// Stringify answer for storage
export const stringifyAnswer = (connections) => {
  return JSON.stringify({ connections });
};

// Compare two connections for equality
export const connectionsEqual = (a, b) => {
  return a.from === b.from && a.to === b.to;
};

// Check if two connection arrays are equivalent
export const connectionArraysEqual = (arr1, arr2) => {
  if (arr1.length !== arr2.length) return false;

  const sorted1 = [...arr1].sort((a, b) =>
    a.from === b.from ? a.to.localeCompare(b.to) : a.from.localeCompare(b.from)
  );
  const sorted2 = [...arr2].sort((a, b) =>
    a.from === b.from ? a.to.localeCompare(b.to) : a.from.localeCompare(b.from)
  );

  return sorted1.every((conn, i) => connectionsEqual(conn, sorted2[i]));
};

// Grade mapping answer
export const gradeMapping = (studentAnswer, correctAnswer, config) => {
  const studentConnections = parseAnswer(studentAnswer).connections;
  const correctConnections = parseAnswer(correctAnswer).connections;

  if (config.grading?.mode === 'partial') {
    // Partial credit mode
    let correctCount = 0;
    const details = {
      correct: [],
      incorrect: [],
      missing: [],
    };

    // Check each student connection
    studentConnections.forEach((studentConn) => {
      const isCorrect = correctConnections.some((correctConn) =>
        connectionsEqual(studentConn, correctConn)
      );
      if (isCorrect) {
        correctCount++;
        details.correct.push(studentConn);
      } else {
        details.incorrect.push(studentConn);
      }
    });

    // Find missing connections
    correctConnections.forEach((correctConn) => {
      const found = studentConnections.some((studentConn) =>
        connectionsEqual(studentConn, correctConn)
      );
      if (!found) {
        details.missing.push(correctConn);
      }
    });

    const totalCorrect = correctConnections.length;
    const score = totalCorrect > 0 ? correctCount / totalCorrect : 0;

    return {
      isCorrect: score === 1,
      score,
      feedback:
        score === 1
          ? 'All connections correct!'
          : `${correctCount} of ${totalCorrect} connections correct`,
      details,
    };
  }

  // Exact mode (default)
  const isCorrect = connectionArraysEqual(studentConnections, correctConnections);

  return {
    isCorrect,
    score: isCorrect ? 1 : 0,
    feedback: isCorrect ? 'Correct!' : 'Incorrect - connections do not match',
    details: {
      studentConnections,
      correctConnections,
    },
  };
};

// Get connection status for display (correct, incorrect, missing)
export const getConnectionStatuses = (studentConnections, correctConnections) => {
  const statuses = [];

  // Check student connections
  studentConnections.forEach((studentConn) => {
    const isCorrect = correctConnections.some((correctConn) =>
      connectionsEqual(studentConn, correctConn)
    );
    statuses.push({
      ...studentConn,
      status: isCorrect ? 'correct' : 'incorrect',
    });
  });

  // Find missing connections
  correctConnections.forEach((correctConn) => {
    const found = studentConnections.some((studentConn) =>
      connectionsEqual(studentConn, correctConn)
    );
    if (!found) {
      statuses.push({
        ...correctConn,
        status: 'missing',
      });
    }
  });

  return statuses;
};

// Calculate SVG path for curved arrow between two points
export const calculateArrowPath = (x1, y1, x2, y2) => {
  const midX = (x1 + x2) / 2;
  const controlOffset = Math.abs(x2 - x1) * 0.2;

  // Create a curved path using quadratic bezier
  return `M ${x1} ${y1} Q ${midX} ${y1 - controlOffset}, ${midX} ${(y1 + y2) / 2} T ${x2} ${y2}`;
};

// Calculate straight arrow path
export const calculateStraightArrowPath = (x1, y1, x2, y2) => {
  return `M ${x1} ${y1} L ${x2} ${y2}`;
};
